import {
  CHART_DISPLAY_TYPES,
  ChartDisplayType,
  NUMERIC_AGGREGATIONS,
  NumericAggregation,
  Period,
  PERIODS,
} from "@/shared/domain/metrics";

import { periodForRange, TIME_RANGES, TimeRange } from "./timeRange";

/**
 * URL search-param keys for the whole dashboard view. Persisting every control
 * in the URL lets the entire view (range, period, chart selection, trend tiles)
 * — plus the drill-down dialog (see `drilldownParams`) — restore itself on
 * reload or when returning via browser back after clicking through to a session.
 */
export const DASHBOARD_PARAMS = {
  range: "range",
  period: "period",
  chartMetric: "chartMetric",
  chartAggregation: "chartAgg",
  chartDisplay: "chartDisplay",
  tiles: "tiles",
} as const;

/** Max number of trend tiles, also the number seeded from definitions. */
export const MAX_TILES = 3;

export interface TileConfig {
  name: string;
  aggregation: NumericAggregation;
}

function asTimeRange(value: string | null): TimeRange | null {
  return value !== null && TIME_RANGES.some((t) => t.value === value)
    ? (value as TimeRange)
    : null;
}

function asPeriod(value: string | null): Period | null {
  return value !== null && (PERIODS as readonly string[]).includes(value)
    ? (value as Period)
    : null;
}

function asAggregation(value: string | null): NumericAggregation | null {
  return value !== null &&
    (NUMERIC_AGGREGATIONS as readonly string[]).includes(value)
    ? (value as NumericAggregation)
    : null;
}

function asDisplay(value: string | null): ChartDisplayType | null {
  return value !== null && CHART_DISPLAY_TYPES.some((d) => d.value === value)
    ? (value as ChartDisplayType)
    : null;
}

/** Merge `entries` into a copy of `prev`; a `null` value deletes that key. */
export function mergeParams(
  prev: URLSearchParams,
  entries: Record<string, string | null>,
): URLSearchParams {
  const next = new URLSearchParams(prev);
  for (const [key, value] of Object.entries(entries)) {
    if (value === null) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
  }
  return next;
}

export function readRange(params: URLSearchParams): TimeRange {
  return asTimeRange(params.get(DASHBOARD_PARAMS.range)) ?? "30d";
}

export function readPeriod(params: URLSearchParams, range: TimeRange): Period {
  return asPeriod(params.get(DASHBOARD_PARAMS.period)) ?? periodForRange(range);
}

export function readChartMetric(
  params: URLSearchParams,
  names: string[],
): string {
  const value = params.get(DASHBOARD_PARAMS.chartMetric);
  return value && names.includes(value) ? value : names[0] ?? "";
}

export function readChartAggregation(
  params: URLSearchParams,
): NumericAggregation {
  return asAggregation(params.get(DASHBOARD_PARAMS.chartAggregation)) ?? "mean";
}

export function readChartDisplay(params: URLSearchParams): ChartDisplayType {
  return asDisplay(params.get(DASHBOARD_PARAMS.chartDisplay)) ?? "line";
}

/**
 * Parse the trend-tile configs from the URL (`name:agg,name:agg,...`), keeping
 * only entries whose metric still exists. Falls back to the first
 * {@link MAX_TILES} definitions (aggregated by `mean`) when absent or empty.
 */
export function readTiles(
  params: URLSearchParams,
  names: string[],
): TileConfig[] {
  const raw = params.get(DASHBOARD_PARAMS.tiles);
  if (raw) {
    const parsed = raw
      .split(",")
      .map((part): TileConfig => {
        const [name, agg] = part.split(":");
        return { name, aggregation: asAggregation(agg) ?? "mean" };
      })
      .filter((tile) => tile.name && names.includes(tile.name))
      .slice(0, MAX_TILES);
    if (parsed.length > 0) {
      return parsed;
    }
  }
  return names
    .slice(0, MAX_TILES)
    .map((name) => ({ name, aggregation: "mean" as NumericAggregation }));
}

export function writeTiles(tiles: TileConfig[]): string {
  return tiles.map((tile) => `${tile.name}:${tile.aggregation}`).join(",");
}
